import React, { useEffect, useState, useCallback } from 'react';
import zhTW from 'rsuite/locales/zh_TW';
import { Calendar, List, Badge, Modal, Message, Loader, Tag } from 'rsuite';
import { getFleetCalendarOrders, FleetCalendarOrder } from '../../service/FleetCalendarService';
import { authUserData } from "../../service";
import { Order } from '../../my-type';
import { OrderStatus } from '../../my-enum';
import AdminDashboard from '../../components/AdminDashboard';
import { getOrderTypeKey, getOrderBrandName } from '../../utilities/OrderHelper';
import { useNavigate } from 'react-router-dom';
import { hasRole } from '../../util/roles';


const OrderListCalendar: React.FC = () => {
  const [orders, setOrders] = useState<FleetCalendarOrder[]>([]);
  const [brandDispatchOnly, setBrandDispatchOnly] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [calendarDate, setCalendarDate] = useState<Date>(new Date());
  const authUser = authUserData();
  const canManageOrders = hasRole(authUser?.roleString, "Driver");
  const navigate = useNavigate();

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const yearMonth = `${calendarDate.getFullYear()}-${(calendarDate.getMonth() + 1).toString().padStart(2, '0')}`;
      const response = await getFleetCalendarOrders(authUser?.team.code!, yearMonth);
      setOrders(response);
    } catch (error) {
      setError('無法載入訂單，請重新整理再試。');
    } finally {
      setLoading(false);
    }
  }, [authUser, calendarDate]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const filteredOrders = brandDispatchOnly ? orders.filter(order => order.brandId != null) : orders;

  // Group orders by date string (yyyy-mm-dd)
  const ordersByDate: Record<string, FleetCalendarOrder[]> = {};
  filteredOrders.forEach(order => {
    const dateStr = new Date(order.rideTime).toLocaleDateString('sv-SE', { timeZone: 'Asia/Taipei' });
    if (!ordersByDate[dateStr]) ordersByDate[dateStr] = [];
    ordersByDate[dateStr].push(order);
  });

  // Preserve the existing paid-order and Taipei ride-month selection.
  const currentYear = calendarDate.getFullYear();
  const currentMonth = calendarDate.getMonth();
  const paidOrders = filteredOrders
    .filter(order => {
      const rideDate = new Date(order.rideTime);
      const rideYear = rideDate.toLocaleString('en-US', { timeZone: 'Asia/Taipei', year: 'numeric' });
      const rideMonth = rideDate.toLocaleString('en-US', { timeZone: 'Asia/Taipei', month: '2-digit' });
      return (
        Number(rideYear) === currentYear &&
        Number(rideMonth) === currentMonth + 1 &&
        order.status !== 6 && !(order.brandId && order.status === 2) && order.paymentPaid
      );
    });
  const hasMissingAmount = paidOrders.some(order => order.fleetAmount == null);
  const totalPrice = paidOrders.reduce((sum, order) => sum + (order.fleetAmount ?? 0), 0);

  const renderCell = (date: Date) => {
    const dateStr = date.toLocaleDateString('sv-SE', { timeZone: 'Asia/Taipei' });
    //const count = ordersByDate[dateStr]?.length || 0;
    const pendingPayCount = ordersByDate[dateStr]?.filter(order => order.status === 0 && !order.paymentPaid && !order.isAdminOrder).length || 0;
    const pendingCount = ordersByDate[dateStr]?.filter(order => order.status === 0 && order.paymentPaid && !order.brandCancellationPending).length || 0;
    const ongoingCount = ordersByDate[dateStr]?.filter(order => !order.brandCancellationPending && (order.status === 1 || order.status === 3 || order.status === 4)).length || 0;
    const completedCount = ordersByDate[dateStr]?.filter(order => order.status === 5).length || 0;
    const pendingRefundCount = ordersByDate[dateStr]?.filter(order => order.status === 2 && order.paymentPaid && !order.brandId).length || 0;
    const cancelledCount = ordersByDate[dateStr]?.filter(order => order.brandId && order.status === 2).length || 0;
    const cancellationPendingCount = ordersByDate[dateStr]?.filter(order => order.brandCancellationPending).length || 0;
    const refundedCount = ordersByDate[dateStr]?.filter(order => order.status === 6).length || 0;
    if (pendingPayCount === 0 && pendingCount === 0 && ongoingCount === 0 && completedCount === 0 && pendingRefundCount === 0 && refundedCount === 0 && cancelledCount === 0 && cancellationPendingCount === 0) return null;
    return (
      <div className="fleet-calendar-counts">
        {cancelledCount > 0 && <Badge content={`已取消 ${cancelledCount}`} style={{ background: '#667085' }} />}
        {cancellationPendingCount > 0 && <Badge content={`取消審核中 ${cancellationPendingCount}`} style={{ background: '#b54708' }} />}
        {pendingPayCount > 0 && (
          <Badge content={`未付款 ${pendingPayCount}`} style={{ background: '#667085' }} />
        )}
        {pendingCount > 0 && (
          <Badge content={`未指派 ${pendingCount}`} style={{ background: '#b42318' }} />
        )}
        {ongoingCount > 0 && (
          <Badge content={`已指派 ${ongoingCount}`} style={{ background: '#175cd3' }} />
        )}
        {completedCount > 0 && (
          <Badge content={`已完成 ${completedCount}`} style={{ background: '#067647' }} />
        )}
        {pendingRefundCount > 0 && (
          <Badge content={`待退款 ${pendingRefundCount}`} style={{ background: '#b54708' }} />
        )}
        {refundedCount > 0 && (
          <Badge content={`已退款 ${refundedCount}`} style={{ background: '#667085' }} />
        )}
      </div>
    );
  };

  const handleSelect = (date: Date) => {
    setSelectedDate(date);
    setShowModal(true);
  };

  // Update calendarDate when user navigates months
  const handleCalendarChange = (date: Date) => {
    setCalendarDate(date);
  };

  const selectedDateStr = selectedDate ? selectedDate.toLocaleDateString('sv-SE', { timeZone: 'Asia/Taipei' }) : '';
  const ordersForSelectedDate = selectedDateStr ? ordersByDate[selectedDateStr] || [] : [];

  const getOrderStatusDescriptionAndColor = (order: Order) => {
    let description = 'N/A';
    let color = '#ddd';

    if (order.brandCancellationPending) {
      description = '取消審核中，暫停履約'; color = '#b54708';
    } else if (order.status === OrderStatus.派車中 && !order.paymentPaid && !order.isAdminOrder) {
      description = '未付款未指派';
      color = '#667085';
    }
    else if (order.status === OrderStatus.派車中 && order.paymentPaid) {
      description = order.isAdminOrder ? '管理單未指派' : '已付款未指派';
      color = '#b42318';
    } else if (order.status === OrderStatus.已派車) {
      description = '已指派';
      color = '#175cd3';
    } else if (order.status === OrderStatus.已出發) {
      description = '已出發';
      color = '#175cd3';
    } else if (order.status === OrderStatus.載客中) {
      description = '載客中';
      color = '#175cd3';
    } else if (order.status === OrderStatus.已完成) {
      description = '已完成';
      color = '#067647';
    } else if (order.status === OrderStatus.已取消 && order.paymentPaid) {
      description = order.brandId ? '已取消（退款由品牌處理）' : '已取消待退款';
      color = '#b54708';
    } else if (order.status === OrderStatus.已退款) {
      description = '已退款';
      color = '#667085';
    } else {
      description = 'N/A';
      color = '#ddd';
    }
    return { description, color };
  };

  return (
    <AdminDashboard title="訂單月曆">
      <div className="fleet-toolbar">
        <div className="fleet-actions">
          <button className="fleet-button" onClick={fetchOrders} disabled={loading}>{loading ? '載入中…' : '重新整理'}</button>
          {canManageOrders && <button className="fleet-button fleet-button--primary" onClick={() => navigate('/orders/create')}>＋ 新增訂單</button>}
          <label className="d-flex align-items-center gap-2">
            <input type="checkbox" checked={brandDispatchOnly} onChange={event => setBrandDispatchOnly(event.target.checked)} />
            僅顯示品牌派單
          </label>
        </div>
        <span>本月已付款金額 <strong>NT$ {loading || error || hasMissingAmount ? '—' : totalPrice.toLocaleString('zh-TW')}</strong></span>
      </div>
      {!loading && !error && hasMissingAmount && <Message type="warning">部分品牌訂單缺少可用的承接金額，暫不顯示本月總額。</Message>}
      {error && <Message type="error" showIcon>{error}</Message>}
      {loading ? <div className="fleet-empty" role="status"><Loader content="載入訂單月曆中…" /></div> : error ? null : (
        <div>
          <div className="fleet-calendar-legend" aria-label="訂單狀態圖例">
            {[['未付款', '#667085'], ['未指派', '#b42318'], ['已指派', '#175cd3'], ['已完成', '#067647'], ['待退款', '#b54708'], ['已退款', '#667085']].map(([label, color]) => <span key={label} style={{ '--legend-color': color } as React.CSSProperties}>{label}</span>)}
            <span>時間以台北時區顯示</span>
          </div>
          <div className="fleet-calendar-scroll" tabIndex={0} role="region" aria-label="訂單月曆，可橫向捲動">
          <Calendar
            locale={zhTW.Calendar}
            bordered
            renderCell={renderCell}
            onSelect={handleSelect}
            onChange={handleCalendarChange}
            value={calendarDate}
          />
          </div>
          <Modal open={showModal} onClose={() => setShowModal(false)} size="sm">
            <Modal.Header>
              <Modal.Title>{selectedDateStr} 訂單</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {ordersForSelectedDate.length === 0 ? (
                <div>當日無訂單</div>
              ) : (
                <List bordered>
                  {ordersForSelectedDate.map(order => (
                    <List.Item key={order.orderID}>
                      <button type="button" className="fleet-day-order" onClick={() => navigate(`/orders/${order.orderID}`)} style={{ '--order-color': getOrderStatusDescriptionAndColor(order).color } as React.CSSProperties}>
                        <b>訂單編號:</b> {order.orderID} {order.brandId != null && <Tag color="blue">品牌派單</Tag>}<br />
                        {order.brandId != null && <><b>品牌名稱:</b> {getOrderBrandName(order)}<br /></>}
                        <b>乘客:</b> {order.passengerName} ({order.passengerPhone})<br />
                        <b>類型:</b> {getOrderTypeKey(order.orderType)}<br />
                        {order.relatedOrderID && (
                          <>
                            <b>關聯訂單:</b> {order.relatedOrderID}<br />
                          </>
                        )}
                        <b>狀態:</b> {getOrderStatusDescriptionAndColor(order).description}<br />
                        <b>{order.brandId != null ? '車隊承接金額:' : '金額:'}</b> {order.fleetAmount == null ? '尚無可用金額' : `NT$ ${order.fleetAmount.toLocaleString('zh-TW')}`}
                      </button>
                    </List.Item>
                  ))}
                </List>
              )}
            </Modal.Body>
          </Modal>
        </div>
      )}
    </AdminDashboard>
  );
};

export default OrderListCalendar;
